import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import orgApi from '../api/organizations';
import { useAuth } from '../auth/AuthProvider';

const InvitationAccept = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState('loading'); // loading | success | needs_registration | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    let timer;
    const token = searchParams.get('token');
    if (!token) {
      setState('error');
      setMessage('Invalid invitation link — no token found.');
      return;
    }
    orgApi.acceptInvitation(token)
      .then(data => {
        if (data.success) {
          setState('success');
          if (isAuthenticated) {
            timer = setTimeout(() => navigate('/'), 2500);
          } else {
            timer = setTimeout(() => navigate('/login?redirect=/'), 2500);
          }
        } else if (data.requires_registration) {
          setState('needs_registration');
          setMessage(`You've been invited to join ${data.org_name} as ${data.role}. Create an account to continue.`);
          timer = setTimeout(() => {
            navigate(`/login?register=1&email=${encodeURIComponent(data.email)}&org=${encodeURIComponent(data.org_name)}`);
          }, 3000);
        }
      })
      .catch(err => {
        setState('error');
        setMessage(err.response?.data?.detail || 'This invitation is invalid or has expired.');
      });
    return () => clearTimeout(timer);
  }, [searchParams, isAuthenticated, navigate]);

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
        <div className="flex justify-center mb-6">
          <Shield className="w-10 h-10 text-cyan-400" />
        </div>
        <h1 className="text-xl font-bold text-white mb-2">UM-DragonEye</h1>

        {state === 'loading' && (
          <>
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mt-6" />
            <p className="text-gray-400 mt-3 text-sm">Validating your invitation…</p>
          </>
        )}
        {state === 'success' && (
          <>
            <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mt-6" />
            <p className="text-emerald-400 font-semibold mt-3">Invitation accepted!</p>
            <p className="text-gray-400 text-sm mt-1">Redirecting you to the app…</p>
          </>
        )}
        {state === 'needs_registration' && (
          <>
            <CheckCircle className="w-8 h-8 text-cyan-400 mx-auto mt-6" />
            <p className="text-gray-300 text-sm mt-3">{message}</p>
            <p className="text-gray-500 text-xs mt-2">Redirecting to registration…</p>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle className="w-8 h-8 text-red-400 mx-auto mt-6" />
            <p className="text-red-400 font-semibold mt-3">Invitation error</p>
            <p className="text-gray-400 text-sm mt-1">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 px-5 py-2 bg-cyan-400/10 text-cyan-400 border border-cyan-400/30 rounded-lg text-sm font-semibold hover:bg-cyan-400/20 transition-colors"
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default InvitationAccept;
